import React, { useContext, useEffect, useState, useRef } from 'react';
import { List, ListHeader, DataColumn } from 'components/lists';
import { useAggregate, useFind } from 'hooks';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Context as AlertContext } from 'contexts/alert';
import { Config } from 'configs';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from 'components/layouts/pageTitle';
import { DropDownList } from 'components/formInputs/dropDownList';
import { PageContent } from 'components/layouts/pageContent';
import { FilterBlock, FilterField } from 'components/layouts/filterBlock';
import { ListLayout } from 'components/layouts/listLayout';
import { Pagination } from 'components/lists/pagination';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { NoRestaurantsBlock } from 'components/blocks/noRestaurants';
import { extractRecordsFromAggreagte } from 'utils/renderingHelpers';
import { QrCode } from 'components/blocks/qrCode';
import { DropdownWrapper } from 'components/formInputs/wrappers/dropdownWrapper';

export const Tables = () => {
  const { state: auth } = useContext(AuthContext);
  const { state: companyState, getCompanyRestaurants, editById, shouldDisplayNoRestaurantBlock } = useContext(CompanyContext);
  const { alertController } = useContext(AlertContext);

  const [currentPage, setCurrentPage] = useState(1);

  const [filterRestaurant, setFilterRestaurant, filterRestaurantRef] = useLocalStorage(`tables-filter-restaurant-${auth.company?.company}`, null);
  const [currentSearchedRestaurant, setCurrentSearchedRestaurant] = useState(null);

  const [sort] = useLocalStorage(`tables-sort-${auth.company?.company}`, { field: 'date', dbField: 'date', order: -1 });
  const changedSort = useRef({ value: null }).current;

  const qrRef = useRef(null);

  const navigate = useNavigate();

  const employeesRequestData = {
    collectionName: Config.DATA.COLLECTIONS.EMPLOYEES,
    secretKey: auth.token,
    filterBuilder: () => {
      const filter = { company: { $oid: auth.company?.company } };

      if (filterRestaurantRef.value) {
        filter.restaurant = { $oid: filterRestaurantRef.value };
      }

      return filter;
    },
    projection: {
      _id: 1,
      first_name: 1,
      last_name: 1,
    },
    excludeCulture: true,
    pageSize: 100,
    pageNumber: 0,
    loadOnInit: false,
    noFindOnLoad: true,
    resourceV2: 'AdminEmployee',
    companyIdForV2: auth.company?.company,
  };

  const [findEmployees, employees, isEmployeesLoading] = useFind(employeesRequestData);

  // get employees on init if restaurant selected
  useEffect(() => {
    if (filterRestaurantRef.value) {
      findEmployees();
    }
  }, []);

  const requestData = {
    collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
    aggregateId: Config.DATA.AGGREGATES.GET_TABLES,
    secretKey: auth.token,
    filterBuilder: () => {
      const filter = { companyId: auth.company?.company };

      if (filterRestaurantRef.value) {
        filter.restaurantFilter = `{ _id: { $eq: ObjectId('${filterRestaurantRef.value}') } }`;
      } else {
        filter.restaurantFilter = {};
      }

      setCurrentSearchedRestaurant(filterRestaurantRef.value);

      const sortKey = changedSort.value?.dbField || sort?.dbField || 'number_sort';

      if (Array.isArray(sortKey)) {
        filter.sort = {};
        sortKey.forEach((key) => {
          filter.sort[`'${key}'`] = changedSort.value?.order || sort?.order || -1;
        });
      } else {
        filter.sort = { [`${sortKey}`]: changedSort.value?.order || sort?.order || -1 };
      }

      return filter;
    },
    pageSize: Config.DEFAULTS.PAGE_SIZE,
    pageNumber: currentPage - 1,
    loadOnInit: true,
    aggregateIdV2: 'AdminListTables',
    companyIdForV2: auth.company?.company,
  };

  const [fetchTables, tablesResponse, isLoading] = useAggregate(requestData);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const onFilterTables = () => {
    fetchTables();

    if (filterRestaurantRef.value) {
      findEmployees(null, true);
    }
  };

  const onClearFilters = () => {
    setFilterRestaurant('');
    onFilterTables();
  };

  const onEditClicked = (item) => {
    navigate(`/settings/tables/${item._id}`);
  };

  const onDownloadClicked = async (item) => {
    qrRef.current.renderAndDownload(
      `${item.url}?type=table&scanId=${item.qr}`,
      `table-${item.number}`,
    );
  };

  const onEmployeeSet = async (tableId, employeeId) => {
    if (!tableId) {
      return;
    }

    await editById(
      {
        collectionName: Config.DATA.COLLECTIONS.TABLES,
        id: tableId,
        updateClause: employeeId ? { $set: { assigned_employee: { $oid: employeeId } } } : { $set: { assigned_employee: null } },
        token: auth.token,
        ignoreTriggers: true,
        resourceV2: 'AdminTable',
        updateTypeV2: 'AssignedEmployee',
        updateDataV2: employeeId ? { assigned_employee: employeeId } : { assigned_employee: null },
        companyIdForV2: auth.company?.company,
      },
      () => {
        alertController.success({ text: 'Table employee assigned' });
      },
    );
  };

  if (isLoading !== Config.DATA.LOADING_STATUS.INITIAL_LOAD && shouldDisplayNoRestaurantBlock()) {
    return <NoRestaurantsBlock />;
  }

  const { paginatedResults: tables, totalCount } = extractRecordsFromAggreagte(tablesResponse);

  return (
    <ListLayout isLoading={isLoading === Config.DATA.LOADING_STATUS.INITIAL_LOAD || companyState.isLoadingRestaurants}>
      <PageTitle title="Tables" breadcrumbs={[{ path: '/settings', title: 'Settings' }]} isTopLevel />
      <PageContent>
        <FilterBlock buttons={[
          {
            onPress: onClearFilters,
            text: 'Clear',
            class: 'bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 inline-flex justify-center items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50',
            disabled: !!isLoading,
          },
          {
            onPress: onFilterTables,
            text: 'Search',
            class: 'ml-3 bg-indigo-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center items-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50',
            disabled: !!isLoading,
            isLoading: !!isLoading,
          },
        ]}
        >
          <FilterField>
            <DropDownList
              showLabel
              id="restaurants"
              name="restaurants"
              label="Restaurant"
              value={filterRestaurant}
              onChange={(e) => {
                setFilterRestaurant(e.target.value);
              }}
              options={[{ label: 'All', value: '' }, ...(companyState.restaurants || []).map((restaurant) => ({
                label: restaurant.name,
                value: restaurant._id,
              }))]}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            />
          </FilterField>
        </FilterBlock>
        <ListHeader
          title="Overview"
          description={`Tables of ${auth.company?.display_name}`}
        >
          <button
            type="button"
            onClick={() => navigate(`/settings/tables/add-new?restaurant=${filterRestaurant}`)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Add new
          </button>
        </ListHeader>
        <List source={tables} onRowClick={onEditClicked}>
          <DataColumn
            name="Name"
            headerClass="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            dbColumnName="name"
            dataCellClass="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6"
          />
          <DataColumn
            name="Number"
            headerClass="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            dbColumnName="number"
            dataCellClass="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6"
          />
          <DataColumn
            name="Section"
            headerClass="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            dbColumnName="area_name"
            dataCellClass="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6"
          />
          {!!currentSearchedRestaurant && (
          <DataColumn
            name="Waiter"
            render={(item) => {
              return (
                <DropdownWrapper
                  key={`${isEmployeesLoading}`}
                  options={isEmployeesLoading === 0 ? [{ label: '-', value: '' }, ...(employees || []).map((employee) => ({
                    label: `${employee.first_name} ${employee.last_name}`.trim(),
                    value: employee._id,
                  }))] : [{ label: '-', value: '' }]}
                  onChange={(e) => {
                    onEmployeeSet(item._id, e.target.value);
                  }}
                  defaultValue={item.assigned_employee}
                  responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-6"
                />
              );
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            headerClass="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            dbColumnName="waiter"
            dataCellClass="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6"
          />
          )}
          <DataColumn
            name="QR Code"
            dbColumnName="url"
            headerClass="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            dataCellClass="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6"
            title="Download"
            render={(item) => {
              if (item.url) {
                return (
                  // eslint-disable-next-line jsx-a11y/anchor-is-valid
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDownloadClicked(item);
                    }}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    Download
                  </a>
                );
              }

              return <p className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">Client not setup by Waitless</p>;
            }}
            // onClick={item.url ? onDownloadClicked : undefined}
          />
          <Pagination
            pageSize={Config.DEFAULTS.PAGE_SIZE}
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChanged={async (pageNumber) => {
              const request = { ...requestData, pageNumber: pageNumber - 1 };

              await fetchTables(false, request);
              setCurrentPage(pageNumber);
            }}
          />
        </List>
      </PageContent>
      <QrCode ref={qrRef} noRender />
    </ListLayout>
  );
};
